import { BoundaryDraw, INACTIVE_COLOR } from "components/boundary/draw/boundary-draw";
import { DEFAULT_MAP_VIEW } from "components/basemap/basemap";
import { STATIC_DRAW_MODE } from "components/boundary/draw/draw-modes";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useQueryClient } from "@tanstack/react-query";
import { bbox } from "@turf/turf";
import { getMapFitPadding } from "@/utils/map-padding";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  useMemo,
  Ref,
  RefObject,
  useEffect,

  useState,
} from "react";
import { Layer, MapRef, Source } from "react-map-gl/maplibre";
import {
  MapStatus,
  MapStatusOverlay,
} from "../../components/map-status-overlay";
import { ClipConfiguration } from "../../models";
import { loadClipWithGeometry, useClipGeometry } from "../../use-clip-geometry";
import { ClipItem } from "./clip-item";

interface ClipPreview {
  key: string;
  data: GeoJSON.FeatureCollection | null;
  extent?: [number, number, number, number];
}

interface RecentClipsProps {
  onSelect: (clip: ClipConfiguration) => void;
  maxClips: number;
  clips: ClipConfiguration[];
  draw: BoundaryDraw;
  mapRef: Ref<MapRef>;
}

export const RecentClips = (props: RecentClipsProps) => {
  const mapRef = props.mapRef as RefObject<MapRef>;
  const queryClient = useQueryClient();
  const { enqueueQueryFailed } = useCustomSnackbars();
  const [selected, setSelected] = useState<ClipConfiguration>();
  const [isOpening, setIsOpening] = useState(false);

  const geometryQuery = useClipGeometry(selected);

  // Not the draw control (terra-draw overflows on rings >100k vertices); keyed per clip so the source is re-added.
  const preview = useMemo<ClipPreview | null>(() => {
    if (!selected) return null;
    // Wait for the download: swapping data on a mounted source stopped the map redrawing.
    if (!geometryQuery.isSuccess) return null;
    const geometry = geometryQuery.data?.geometry?.features.length
      ? geometryQuery.data.geometry
      : null;
    return {
      key: `clip-preview-${selected.id}`,
      data: geometry,
      extent: geometry ? (bbox(geometry) as [number, number, number, number]) : undefined,
    };
  }, [selected, geometryQuery.isSuccess, geometryQuery.data]);

  const handleSelect = (clip: ClipConfiguration) => {
    setSelected(clip);
    // Clear any editable features left by a previously opened clip.
    props.draw.deleteAll();
    props.draw.changeMode(STATIC_DRAW_MODE);
  };

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !preview) return;
    const { extent } = preview;
    if (!extent) {
      map.flyTo({
        center: [DEFAULT_MAP_VIEW.longitude, DEFAULT_MAP_VIEW.latitude],
        zoom: DEFAULT_MAP_VIEW.zoom,
        duration: 1000,
      });
    } else {
      map.fitBounds(
        [
          [extent[0], extent[1]],
          [extent[2], extent[3]],
        ],
        { padding: getMapFitPadding(map.getContainer(), 20), duration: 1000 },
      );
    }
  }, [preview]);

  const handleOpen = async (clip: ClipConfiguration) => {
    setIsOpening(true);
    try {
      const detailedClip = await loadClipWithGeometry(queryClient, clip.id);
      props.onSelect(detailedClip);
    } catch (error) {
      enqueueQueryFailed(String(error));
    } finally {
      setIsOpening(false);
    }
  };

  const mapStatus: MapStatus =
    isOpening || (selected && geometryQuery.isFetching)
    ? "loading"
    : selected?.geometryProcessing === "processing"
      ? "processing"
      : geometryQuery.isError
        ? "error"
        : null;

  dayjs.extend(utc);

  return (
    <div className="flex flex-col gap-2">
      {preview?.data && (
        <Source
          key={preview.key}
          id={preview.key}
          type="geojson"
          data={preview.data}
        >
          <Layer
            id={`${preview.key}-fill`}
            type="fill"
            paint={{ "fill-color": INACTIVE_COLOR, "fill-opacity": 0.1 }}
          />
          <Layer
            id={`${preview.key}-line`}
            type="line"
            paint={{
              "line-color": INACTIVE_COLOR,
              "line-width": 2,
            }}
          />
        </Source>
      )}
      <MapStatusOverlay mapRef={mapRef} status={mapStatus} />
      {props.clips.slice(0, props.maxClips).map((clip: ClipConfiguration) => (
        <ClipItem
          isSelected={selected?.id === clip.id}
          key={clip.id}
          clip={clip}
          onOpen={handleOpen}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
};
